import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// Traduzir erros 422 (Pydantic) para mensagens amigáveis em PT-BR
export const formatarErroAPI = (error) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    const campoMap = {
      'telefone_venda': 'Telefone da Venda',
      'cliente': 'Dados do Cliente',
      'cliente.email': 'E-mail',
      'cliente.documento': 'CPF/CNPJ',
      'cliente.nome_razao_social': 'Nome/Razão Social',
      'cliente.telefone1': 'Telefone 1',
      'cliente.tipo_documento': 'Tipo Documento',
      'endereco': 'Endereço',
      'endereco.cep': 'CEP',
      'endereco.endereco': 'Logradouro',
      'endereco.numero': 'Número',
      'endereco.cidade': 'Cidade',
      'endereco.estado': 'Estado',
      'empresa_id': 'Empresa',
      'valor_plano': 'Valor do Plano',
      'tipo_venda': 'Tipo de Venda',
      'pagamento': 'Pagamento',
      'pagamento.forma_pagamento': 'Forma de Pagamento',
      'pagamento.vencimento_fatura': 'Vencimento',
      'modalidade_venda': 'Modalidade',
    };
    const erros = detail.map(e => {
      const loc = (e.loc || []).filter(l => l !== 'body');
      const campo = loc.join('.');
      const nome = campoMap[campo] || campo || 'campo';
      if (e.type === 'missing') return `${nome}: obrigatório`;
      if (e.type === 'value_error' || e.type === 'string_type') return `${nome}: valor inválido`;
      return `${nome}: ${e.msg || 'dados divergentes'}`;
    });
    return `Verifique os dados: ${erros.slice(0, 3).join(' • ')}`;
  }
  if (typeof detail === 'string') return detail;
  return 'Erro ao processar. Verifique os dados e tente novamente.';
};

// Máscara de telefone BR: (00) 0000-0000 ou (00) 00000-0000
export const formatarTelefoneBR = (valor) => {
  const d = (valor || '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

// Configurar axios
const api = axios.create({
  baseURL: API_BASE,
});

// Interceptor para adicionar token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor para tratar erros
const MSG_SERVIDOR = 'Servidor temporariamente indisponível. Tente novamente em alguns segundos.';
const isFalhaServidor = (error) => {
  if (!error.response) return true;
  const { status, data } = error.response;
  if (status >= 500) return true;
  return typeof data === 'string' && (data.includes('<!DOCTYPE') || data.includes('<html') || data.includes('Cloudflare'));
};

// Só derruba a sessão quando o 401 é realmente de token (inválido/expirado/usuário inativo).
// Qualquer outro 401 (ex.: senha errada em alguma confirmação) vira um erro normal, sem deslogar.
const isSessaoInvalida = (error) => {
  const url = error.config?.url || '';
  if (url.includes('/auth/login')) return false;
  const detail = String(error.response?.data?.detail || '').toLowerCase();
  if (!detail) return true;
  return /credenciais inv|token|not authenticated|expirad|sess[aã]o|usu[aá]rio inativo|desativad/.test(detail);
};

let _redirecionandoLogin = false;
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && isSessaoInvalida(error)) {
      if (!_redirecionandoLogin) {
        _redirecionandoLogin = true;
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
    const config = error.config || {};
    if (isFalhaServidor(error)) {
      // Repetir 1x apenas leituras (GET) quando o servidor/proxy falhou (5xx, 52x Cloudflare, rede)
      if ((config.method || '').toLowerCase() === 'get' && !config.__retried) {
        config.__retried = true;
        await new Promise((r) => setTimeout(r, 1500));
        return api.request(config);
      }
      // Nunca expor HTML do Cloudflare ao usuário
      if (error.response) {
        error.response.data = { detail: MSG_SERVIDOR };
      } else {
        error.response = { status: 0, data: { detail: 'Sem conexão com o servidor. Verifique sua internet.' } };
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// API Calls
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  alterarSenha: (data) => api.post('/auth/alterar-senha', data),
};

export const usersAPI = {
  list: () => api.get('/users'),
  listForChat: () => api.get('/users/chat'),
  listVendedores: () => api.get('/users/vendedores'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  excluirTodos: () => api.delete('/users/excluir-todos'),
  importar: (formData) => api.post('/users/importar-excel', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportar: () => api.get('/users/exportar-excel', { responseType: 'blob' }),
};

export const empresasAPI = {
  list: () => api.get('/empresas'),
  create: (data) => api.post('/empresas', data),
  update: (id, data) => api.put(`/empresas/${id}`, data),
  delete: (id) => api.delete(`/empresas/${id}`),
};

export const produtosAPI = {
  list: () => api.get('/produtos'),
  create: (data) => api.post('/produtos', data),
  update: (id, data) => api.put(`/produtos/${id}`, data),
  delete: (id) => api.delete(`/produtos/${id}`),
};

export const configSistemaAPI = {
  get: () => api.get('/config/sistema'),
  update: (data) => api.put('/config/sistema', data),
};

export const vendasAPI = {
  devolver: (id, motivo) => api.put(`/vendas/${id}/devolver`, { motivo }),
  list: (params) => api.get('/vendas', { params }),
  create: (data) => api.post('/vendas', data),
  get: (id) => api.get(`/vendas/${id}`),
  update: (id, data) => api.put(`/vendas/${id}`, data),
  delete: (id, senha) => api.delete(`/vendas/${id}`, { data: { senha } }),
  excluirTodas: () => api.delete('/vendas/excluir-todas'),
  corrigir: (id, data) => api.put(`/vendas/${id}/corrigir`, data),
  bloquear: (id) => api.put(`/vendas/${id}/bloquear`),
  desbloquear: (id) => api.put(`/vendas/${id}/desbloquear`),
  liberarDuplicidade: (id) => api.post(`/vendas/${id}/liberar-duplicidade`),
  addObservacao: (id, observacao) => api.post(`/vendas/${id}/observacao`, null, { params: { observacao } }),
  trocarVendedor: (id, data) => api.put(`/vendas/${id}/trocar-vendedor`, data),
  exportar: (data) => api.post('/vendas/exportar', data, { responseType: 'blob' }),
  exportarTodas: () => api.post('/vendas/exportar-todas', {}, { responseType: 'blob' }),
  downloadTemplate: () => api.get('/vendas/template/download', { responseType: 'blob' }),
  importar: (formData) => api.post('/vendas/importar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  verificarCorrelacao: (dados) => api.post('/vendas/verificar-correlacao', dados),
  // Anexos
  uploadAnexo: (vendaId, formData) => api.post(`/vendas/${vendaId}/anexos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  downloadAnexo: (vendaId, anexoId) => api.get(`/vendas/${vendaId}/anexos/${anexoId}`, { responseType: 'blob' }),
  excluirAnexo: (vendaId, anexoId) => api.delete(`/vendas/${vendaId}/anexos/${anexoId}`),
};

export const jornadaAPI = {
  registrar: (data) => api.post('/jornada/registrar', data),
  minha: (params) => api.get('/jornada/minha', { params }),
  todas: (params) => api.get('/jornada/todas', { params }),
};

export const cepAPI = {
  buscar: (cep) => api.get(`/cep/${cep}`),
};

export const chatAPI = {
  enviar: (data) => api.post('/chat/mensagem', data),
  obter: (vendaId) => api.get(`/chat/${vendaId}`),
};

export const chatInternoAPI = {
  listarConversas: () => api.get('/chat-interno/conversas'),
  obterMensagens: (destinatarioId) => api.get(`/chat-interno/mensagens/${destinatarioId}`),
  enviarMensagem: (data) => api.post('/chat-interno/enviar', data),
  contarNaoLidas: () => api.get('/chat-interno/nao-lidas'),
};

// Banner CRM API
export const aparenciaAPI = {
  obterLogin: () => api.get('/aparencia/login'),
  enviarImagemLogin: (formData) => api.post('/aparencia/login/imagem', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  restaurarImagemLogin: () => api.delete('/aparencia/login/imagem'),
};

export const bannersAPI = {
  obterAtivo: () => api.get('/banners/ativo'),
  listar: () => api.get('/banners'),
  criar: (data) => api.post('/banners', data),
  atualizar: (id, data) => api.put(`/banners/${id}`, data),
  excluir: (id) => api.delete(`/banners/${id}`),
  toggleAtivo: (id) => api.put(`/banners/${id}/toggle-ativo`),
  uploadImagem: (formData) => api.post('/banners/upload-imagem', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadVideo: (formData) => api.post('/banners/upload-video', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
};

// Mailing API
export const mailingAPI = {
  listar: (params) => api.get('/mailing', { params }),
  criar: (data) => api.post('/mailing', data),
  importar: (formData, tipoMailing = 'PROPOSTA', vendedoresIds = null) => {
    let url = `/mailing/importar?tipo_mailing=${tipoMailing}`;
    if (vendedoresIds) url += `&vendedores_ids=${vendedoresIds}`;
    return api.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  exportar: () => api.post('/mailing/exportar', {}, { responseType: 'blob' }),
  atribuir: (id, vendedorId) => api.put(`/mailing/${id}/atribuir`, { vendedor_id: vendedorId }),
  trabalhar: (id) => api.put(`/mailing/${id}/trabalhar`),
  atualizarObservacao: (id, observacao) => api.put(`/mailing/${id}/observacao`, { observacao }),
  fecharVenda: (id) => api.post(`/mailing/${id}/fechar-venda`),
  negar: (id) => api.put(`/mailing/${id}/negar`),
  tratar: (id, status_atendimento, observacao, extraFields = {}) => api.put(`/mailing/${id}/tratar`, { status_atendimento, observacao, ...extraFields }),
  excluir: (id) => api.delete(`/mailing/${id}`),
  excluirTodos: () => api.delete('/mailing/excluir-todos'),
  estatisticas: () => api.get('/mailing/estatisticas'),
};

// Aniversários API
export const aniversarioAPI = {
  hoje: () => api.get('/aniversariantes-hoje'),
  mensagens: (userId) => api.get(`/aniversario/${userId}/mensagens`),
  enviarMensagem: (userId, mensagem) => api.post(`/aniversario/${userId}/mensagens`, { mensagem }),
};


// Cancelamentos BKO API
export const cancelamentosAPI = {
  listar: (params) => api.get('/cancelamentos', { params }),
  atualizar: (id, data) => api.put(`/cancelamentos/${id}`, data),
  estatisticas: () => api.get('/cancelamentos/estatisticas'),
};

// Aba Móvel (1 registro por chip)
export const movelAPI = {
  listar: (params) => api.get('/movel/linhas', { params }),
  atualizar: (id, data) => api.put(`/movel/linhas/${id}`, data),
  exportar: (params) => api.get('/movel/exportar', { params, responseType: 'blob' }),
  pendentes: (params) => api.get('/movel/pendentes', { params }),
  daVenda: (vendaId) => api.get(`/movel/venda/${vendaId}`),
  notificarPendentes: () => api.post('/movel/pendentes/notificar'),
};

// Sales Credentials API
export const salesCredentialsAPI = {
  listar: () => api.get('/sales-credentials'),
  criar: (data) => api.post('/sales-credentials', data),
  atualizar: (id, data) => api.put(`/sales-credentials/${id}`, data),
  excluir: (id) => api.delete(`/sales-credentials/${id}`),
};

// Equipes de Vendas API
export const equipesAPI = {
  listar: () => api.get('/equipes'),
  criar: (data) => api.post('/equipes', data),
  atualizar: (id, data) => api.put(`/equipes/${id}`, data),
  excluir: (id) => api.delete(`/equipes/${id}`),
};

// Metas de Vendas API
export const metasAPI = {
  listar: (params) => api.get('/metas', { params }),
  minha: () => api.get('/metas/minha'),
  criar: (data) => api.post('/metas', data),
  atualizar: (id, data) => api.put(`/metas/${id}`, data),
  excluir: (id) => api.delete(`/metas/${id}`),
};

// Material de Apoio (educativo) — todos leem/baixam; ADMIN publica
export const materialApoioAPI = {
  listar: (params) => api.get('/material-apoio', { params }),
  enviar: (formData, onProgress) => api.post('/material-apoio', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 300000,
    onUploadProgress: (e) => onProgress && e.total && onProgress(Math.round((e.loaded * 100) / e.total)),
  }),
  arquivo: (id, modo = 'download') => api.get(`/material-apoio/${id}/arquivo`, { params: { modo }, responseType: 'blob', timeout: 300000 }),
  editar: (id, data) => api.put(`/material-apoio/${id}`, data),
  excluir: (id) => api.delete(`/material-apoio/${id}`),
};

// Lei Não Perturbe — consulta de telefones na Conexão Claro
export const naoPerturbeAPI = {
  config: () => api.get('/nao-perturbe/config'),
  alerta: () => api.get('/nao-perturbe/alerta'),
  salvarConfig: (data) => api.put('/nao-perturbe/config', data, { timeout: 60000 }),
  removerConfig: () => api.delete('/nao-perturbe/config'),
  consultar: (telefones) => api.post('/nao-perturbe/consultar', { telefones }, { timeout: 60000 }),
  reconsultarVenda: (vendaId) => api.post(`/vendas/${vendaId}/nao-perturbe/reconsultar`, {}, { timeout: 60000 }),
  reprocessar: (dias = 30) => api.post('/nao-perturbe/reprocessar', { dias }, { timeout: 300000 }),
  conectorParear: () => api.post('/nao-perturbe/conector/parear'),
  conectorRemover: () => api.delete('/nao-perturbe/conector'),
  conectorDownload: () => api.get('/nao-perturbe/conector/download', { responseType: 'blob' }),
  extensaoRemanejarDownload: () => api.get('/nao-perturbe/extensao-remanejar/download', { responseType: 'blob' }),
  extensaoRemanejarInfo: () => api.get('/nao-perturbe/extensao-remanejar/info'),
};

// Viabilidade Claro — consulta de cobertura via conector no navegador
export const viabilidadeAPI = {
  config: () => api.get('/viabilidade/config'),
  salvarAgente: (agente_autorizado_id) => api.put('/viabilidade/agente', { agente_autorizado_id }),
  complementos: (data) => api.post('/viabilidade/complementos', data, { timeout: 90000 }),
  cidades: (uf) => api.get('/viabilidade/cidades', { params: { uf }, timeout: 60000 }),
  logradouros: (data) => api.post('/viabilidade/logradouros', data, { timeout: 90000 }),
  hps: (data) => api.post('/viabilidade/hps', data, { timeout: 90000 }),
  detalhe: (data) => api.post('/viabilidade/detalhe', data, { timeout: 60000 }),
  planos: (data) => api.post('/viabilidade/planos', data, { timeout: 120000 }),
  historico: () => api.get('/viabilidade/historico'),
  conectorParear: () => api.post('/viabilidade/conector/parear'),
  conectorRemover: () => api.delete('/viabilidade/conector'),
  conectorDownload: () => api.get('/viabilidade/conector/download', { responseType: 'blob' }),
  capturas: () => api.get('/viabilidade/capturas'),
  limparCapturas: () => api.delete('/viabilidade/capturas'),
};

// Checagem de crédito Claro — via conector no navegador
export const creditoAPI = {
  consultar: (data) => api.post('/credito/consultar', data, { timeout: 120000 }),
  historico: (params) => api.get('/credito/historico', { params }),
  reconsultarVenda: (vendaId) => api.post(`/vendas/${vendaId}/credito/reconsultar`, { forcar: true }, { timeout: 120000 }),
};

// Relatório das consultas Conexão Claro (viabilidade + crédito)
export const relatoriosConsultasAPI = {
  consultas: (params) => api.get('/relatorios/consultas', { params, timeout: 60000 }),
  exportar: (params) => api.get('/relatorios/consultas/exportar', { params, responseType: 'blob', timeout: 120000 }),
};

// Data Center — central de indicadores
export const dataCenterAPI = {
  resumo: (params) => api.get('/data-center/resumo', { params, timeout: 60000 }),
  exportar: (params) => api.get('/data-center/exportar', { params, responseType: 'blob', timeout: 120000 }),
};

// Google Sheets — sincronização de vendas (ADMIN/BKO)
export const sheetsAPI = {
  status: () => api.get('/sheets/status'),
  fila: (params) => api.get('/sheets/fila', { params }),
  salvarConfig: (data) => api.put('/sheets/config', data, { timeout: 60000 }),
  alternarAtivo: (ativo) => api.put('/sheets/config/ativo', { ativo }),
  removerConfig: () => api.delete('/sheets/config'),
  testar: () => api.post('/sheets/testar', null, { timeout: 60000 }),
  sincronizarTudo: () => api.post('/sheets/sincronizar-tudo', null, { timeout: 120000 }),
  sincronizarAgora: () => api.post('/sheets/sincronizar-agora', null, { timeout: 120000 }),
  reprocessarErros: () => api.post('/sheets/reprocessar-erros'),
};

// Backups de vendas (ADMIN)
export const backupsAPI = {
  listar: () => api.get('/backups'),
  criar: () => api.post('/backups/vendas', null, { timeout: 180000 }),
  baixar: (id, formato) => api.get(`/backups/${id}/download`, { params: { formato }, responseType: 'blob', timeout: 180000 }),
};

// Sedes (Filiais) API
export const sedesAPI = {
  listar: () => api.get('/sedes'),
  criar: (data) => api.post('/sedes', data),
  atualizar: (id, data) => api.put(`/sedes/${id}`, data),
  inativar: (id) => api.put(`/sedes/${id}/inativar`),
  excluir: (id) => api.delete(`/sedes/${id}`),
  listarUsuarios: (id) => api.get(`/sedes/${id}/usuarios`),
};

// Jornada Controle API
export const jornadaControleAPI = {
  status: () => api.get('/jornada-controle/status'),
  iniciarAlmoco: () => api.post('/jornada-controle/iniciar-almoco'),
  retorno: (senha) => api.post('/jornada-controle/retorno', { senha }),
  logout: () => api.post('/jornada-controle/logout'),
  registros: (params) => api.get('/jornada-controle/registros', { params }),
  exportar: (params) => api.post('/jornada-controle/exportar', null, { params, responseType: 'blob' }),
  // Novos endpoints
  statusJornada: () => api.get('/jornada/status'),
  solicitarHoraExtra: (data = {}) => api.post('/jornada/solicitar-hora-extra', data),
  alternarCronometro: (ocultar) => api.put('/jornada-controle/cronometro', { ocultar }),
  minhaHoraExtra: () => api.get('/jornada/hora-extra/minha'),
  horasExtrasPeriodo: (params) => api.get('/jornada/horas-extras', { params }),
  bancoHoras: (params) => api.get('/jornada-controle/banco-horas', { params }),
  solicitacoesHoraExtra: () => api.get('/jornada/solicitacoes-hora-extra'),
  liberarHoraExtra: (dados) => api.post('/jornada/liberar-hora-extra', dados),
  negarHoraExtra: (dados) => api.post('/jornada/negar-hora-extra', dados),
  relatorioMensal: (params) => api.get('/jornada/relatorio-mensal', { params, responseType: 'blob' }),
};

// Valores (Tabela de Precos) API
export const valoresAPI = {
  listar: (params) => api.get('/valores', { params }),
  criar: (data) => api.post('/valores', data),
  atualizar: (id, data) => api.put(`/valores/${id}`, data),
  excluir: (id) => api.delete(`/valores/${id}`),
};

// Chat IA API
export const chatIaAPI = {
  criarSessao: () => api.post('/chat-ia/sessoes'),
  listarSessoes: () => api.get('/chat-ia/sessoes'),
  excluirSessao: (sessionId) => api.delete(`/chat-ia/sessoes/${sessionId}`),
  listarMensagens: (sessionId) => api.get(`/chat-ia/sessoes/${sessionId}/mensagens`),
  enviarMensagemStream: (sessionId, texto) => {
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE}/chat-ia/sessoes/${sessionId}/mensagem`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ texto }),
    });
  },
};

// Suporte / Tickets API
export const suporteAPI = {
  // Tipos
  listarTipos: () => api.get('/suporte/tipos'),
  criarTipo: (data) => api.post('/suporte/tipos', data),
  atualizarTipo: (id, data) => api.put(`/suporte/tipos/${id}`, data),
  excluirTipo: (id) => api.delete(`/suporte/tipos/${id}`),
  // Tickets
  criarTicket: (data) => api.post('/suporte/tickets', data),
  listarTickets: (params) => api.get('/suporte/tickets', { params }),
  obterTicket: (id) => api.get(`/suporte/tickets/${id}`),
  atualizarStatus: (id, data) => api.put(`/suporte/tickets/${id}/status`, data),
  // Mensagens
  listarMensagens: (ticketId) => api.get(`/suporte/tickets/${ticketId}/mensagens`),
  enviarMensagem: (ticketId, data) => api.post(`/suporte/tickets/${ticketId}/mensagens`, data),
  // Estatísticas
  estatisticas: () => api.get('/suporte/estatisticas'),
};
